import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard, Plus, Pencil, Trash2, X, History,
  ArrowUpFromLine, ArrowDownFromLine, IndianRupee, Upload, FileText,
} from 'lucide-react';
import DataGrid from '../components/DataGrid';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  lender_name: '', lender_type: 'Bank', account_number: '',
  opened_date: today(), sanctioned_limit: '', interest_rate: '',
  interest_period: 'annual', purpose: 'Working Capital',
  notes: '', lender_contact: '',
};

const STATUS_STYLE = {
  Active: 'bg-amber-900/30 text-amber-300 border-amber-700',
  Closed: 'bg-zinc-800 text-zinc-300 border-zinc-700',
};

const TX_STYLE = {
  draw:            { icon: ArrowDownFromLine, color: 'text-cyan-400',  bg: 'bg-cyan-900/30 border-cyan-700',     label: 'Drew' },
  repay:           { icon: ArrowUpFromLine,   color: 'text-green-400', bg: 'bg-green-900/30 border-green-700',   label: 'Repaid' },
  interest_charge: { icon: IndianRupee,       color: 'text-red-400',   bg: 'bg-red-900/30 border-red-700',       label: 'Interest charged' },
};

/**
 * Overdrafts — revolving working-capital facilities.
 *
 * Different from fixed loans: limit + utilization model with many in/out
 * transactions. Each draw inflates Cashflow inflow, each repay outflows.
 * Interest accrues only on utilized balance.
 */
export default function Overdrafts() {
  const [ods, setOds] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [editOd, setEditOd]   = useState(null);
  const [detailOd, setDetailOd] = useState(null);
  const [txFor, setTxFor]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/overdrafts/`, { withCredentials: true }),
        axios.get(`${API}/overdrafts/summary`, { withCredentials: true }),
      ]);
      setOds(a.data || []);
      setSummary(b.data || null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load overdrafts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-4" data-testid="overdrafts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CreditCard className="text-cyan-400" size={22} />
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Overdraft Facilities</h1>
            <p className="text-zinc-500 text-sm">Revolving working-capital lines · interest only on utilized balance</p>
          </div>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-od-btn"
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 rounded-lg text-sm font-semibold">
          <Plus size={14} /> Add OD
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <Card label="Sanctioned" val={fmt(summary.total_sanctioned)} testId="od-sum-sanctioned" />
          <Card label="Utilized" val={fmt(summary.total_utilization)} hot={summary.utilization_pct > 80} testId="od-sum-util" />
          <Card label="Available" val={fmt(summary.total_available)} color="text-green-400" testId="od-sum-avail" />
          <Card label="Utilization %" val={`${summary.utilization_pct}%`} hot={summary.utilization_pct > 80} testId="od-sum-pct" />
          <Card label="Interest Paid" val={fmt(summary.total_interest_paid)} color="text-violet-400" testId="od-sum-paid" />
          <Card label="Accrued" val={fmt(summary.total_accrued)} color="text-amber-400" testId="od-sum-acc" />
        </div>
      )}

      <DataGrid
        testId="ods-grid"
        title="All OD Facilities"
        icon={<CreditCard size={14} className="text-cyan-400" />}
        rows={ods}
        rowKey={(o) => o.id}
        initialSort={{ key: 'opened_date', dir: 'desc' }}
        exportName="overdrafts"
        emptyText={loading ? 'Loading…' : 'No OD facilities yet. Click "Add OD" to start.'}
        columns={[
          { key: 'lender_name', label: 'Lender', type: 'text',
            accessor: o => o.lender_name,
            render: o => (
              <button onClick={() => setDetailOd(o)} data-testid={`open-od-${o.id}`}
                className="text-left hover:underline decoration-cyan-500 decoration-dotted underline-offset-2">
                <div className="text-zinc-100 font-medium text-xs">{o.lender_name}</div>
                <div className="text-[10px] text-zinc-500">{o.account_number || '—'} · {o.purpose}</div>
              </button>
            ) },
          { key: 'sanctioned_limit', label: 'Limit', type: 'number', align: 'right',
            accessor: o => o.sanctioned_limit,
            render: o => <span className="font-mono text-zinc-200 text-xs">{fmt(o.sanctioned_limit)}</span> },
          { key: 'current_utilization', label: 'Used', type: 'number', align: 'right',
            accessor: o => o.current_utilization,
            render: o => <span className="font-mono text-cyan-300 text-xs font-bold">{fmt(o.current_utilization)}</span> },
          { key: 'utilization_pct', label: 'Util %', type: 'number', align: 'right',
            accessor: o => o.utilization_pct,
            render: o => {
              const cls = o.utilization_pct > 80 ? 'text-red-400' : o.utilization_pct > 50 ? 'text-amber-400' : 'text-green-400';
              return <span className={`font-mono text-xs font-bold ${cls}`}>{o.utilization_pct}%</span>;
            } },
          { key: 'available_balance', label: 'Available', type: 'number', align: 'right',
            accessor: o => o.available_balance,
            render: o => <span className="font-mono text-green-400 text-xs">{fmt(o.available_balance)}</span> },
          { key: 'annualized_rate', label: 'Rate', type: 'number', align: 'right',
            accessor: o => o.annualized_rate,
            render: o => <span className="font-mono text-amber-300 text-xs">{o.annualized_rate}%</span> },
          { key: 'accrued_interest_unpaid', label: 'Accrued', type: 'number', align: 'right',
            accessor: o => o.accrued_interest_unpaid,
            render: o => o.accrued_interest_unpaid > 0
              ? <span className="font-mono text-amber-400 text-xs">{fmt(o.accrued_interest_unpaid)}</span>
              : <span className="text-zinc-700 text-xs">—</span> },
          { key: 'total_interest_paid', label: 'Int. Paid', type: 'number', align: 'right',
            accessor: o => o.total_interest_paid,
            render: o => <span className="font-mono text-violet-300 text-xs">{fmt(o.total_interest_paid)}</span> },
          { key: 'status', label: 'Status', type: 'enum',
            accessor: o => o.status,
            render: o => <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLE[o.status] || STATUS_STYLE.Active}`}>{o.status}</span> },
          { key: 'actions', label: '', sortable: false, filterable: false, exportable: false,
            accessor: () => '',
            render: o => (
              <div className="flex items-center gap-1">
                <button onClick={() => setTxFor(o)} data-testid={`od-tx-${o.id}`}
                  className="p-1 rounded text-cyan-400 hover:text-cyan-300 hover:bg-zinc-800" title="Record draw/repay/interest">
                  <IndianRupee size={11}/>
                </button>
                <button onClick={() => setEditOd(o)} data-testid={`od-edit-${o.id}`}
                  className="p-1 rounded text-zinc-400 hover:text-amber-400 hover:bg-zinc-800">
                  <Pencil size={11}/>
                </button>
              </div>
            ) },
        ]}
      />

      {showAdd && <OdModal mode="add" form={EMPTY} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editOd && <OdModal mode="edit" form={editOd} onClose={() => setEditOd(null)} onSaved={() => { setEditOd(null); load(); }} />}
      {txFor && <TxModal od={txFor} onClose={() => setTxFor(null)} onSaved={() => { setTxFor(null); load(); }} />}
      {detailOd && <OdDetail od={detailOd} onClose={() => setDetailOd(null)} onRecordTx={() => { setTxFor(detailOd); setDetailOd(null); }} onEdit={() => { setEditOd(detailOd); setDetailOd(null); }} onChanged={load} />}
    </div>
  );
}

function Card({ label, val, hot, color, testId }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${hot ? 'border-red-700/60 bg-red-950/20' : 'border-zinc-800 bg-zinc-900/60'}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-base font-bold mt-0.5 ${hot ? 'text-red-300' : color || 'text-zinc-100'}`}>{val}</div>
    </div>
  );
}

const inputCls = "w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 text-xs focus:outline-none focus:border-cyan-500";

function OdModal({ mode, form, onClose, onSaved }) {
  const [f, setF] = useState(form);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'add') await axios.post(`${API}/overdrafts/`, f, { withCredentials: true });
      else await axios.put(`${API}/overdrafts/${f.id}`, f, { withCredentials: true });
      toast.success(mode === 'add' ? 'OD facility added' : 'OD updated · change logged');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const onDelete = async () => {
    if (!window.confirm(`Delete OD facility ${f.lender_name}? Cascades to transactions and cashflow.`)) return;
    try {
      await axios.delete(`${API}/overdrafts/${f.id}`, { withCredentials: true });
      toast.success('OD deleted');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <Modal title={mode === 'add' ? 'Add Overdraft Facility' : `Edit · ${f.lender_name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="od-form">
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-zinc-400 text-[10px] block mb-0.5">Lender *</label>
            <input required value={f.lender_name} onChange={e => setF({ ...f, lender_name: e.target.value })}
              data-testid="odf-lender" className={inputCls}/></div>
          <div><label className="text-zinc-400 text-[10px] block mb-0.5">Account No.</label>
            <input value={f.account_number || ''} onChange={e => setF({ ...f, account_number: e.target.value })} className={inputCls}/></div>
          <div><label className="text-zinc-400 text-[10px] block mb-0.5">Opened Date *</label>
            <input required type="date" value={f.opened_date} onChange={e => setF({ ...f, opened_date: e.target.value })} className={inputCls}/></div>
          <div><label className="text-zinc-400 text-[10px] block mb-0.5">Sanctioned Limit (₹) *</label>
            <input required type="number" value={f.sanctioned_limit}
              onChange={e => setF({ ...f, sanctioned_limit: e.target.value })}
              data-testid="odf-limit" className={inputCls}/></div>
          <div><label className="text-zinc-400 text-[10px] block mb-0.5">Interest Rate (%) *</label>
            <input required type="number" value={f.interest_rate} step="any"
              onChange={e => setF({ ...f, interest_rate: e.target.value })}
              data-testid="odf-rate" className={inputCls}/></div>
          <div><label className="text-zinc-400 text-[10px] block mb-0.5">Rate Period</label>
            <select value={f.interest_period} onChange={e => setF({ ...f, interest_period: e.target.value })} className={inputCls}>
              <option value="annual">Annual</option><option value="monthly">Monthly</option>
            </select></div>
          <div><label className="text-zinc-400 text-[10px] block mb-0.5">Purpose</label>
            <input value={f.purpose} onChange={e => setF({ ...f, purpose: e.target.value })} className={inputCls}/></div>
          <div><label className="text-zinc-400 text-[10px] block mb-0.5">Lender Contact</label>
            <input value={f.lender_contact || ''} onChange={e => setF({ ...f, lender_contact: e.target.value })} className={inputCls}/></div>
        </div>
        {mode === 'edit' && (
          <div><label className="text-zinc-400 text-[10px] block mb-0.5">Status</label>
            <select value={f.status || 'Active'} onChange={e => setF({ ...f, status: e.target.value })} className={inputCls}>
              <option value="Active">Active</option><option value="Closed">Closed</option>
            </select></div>
        )}
        <div><label className="text-zinc-400 text-[10px] block mb-0.5">Notes</label>
          <textarea value={f.notes || ''} onChange={e => setF({ ...f, notes: e.target.value })} rows={2} className={inputCls}/></div>
        <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
          {mode === 'edit' ? <button type="button" onClick={onDelete} className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1 px-3 py-1.5"><Trash2 size={11}/> Delete</button> : <div/>}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-zinc-400">Cancel</button>
            <button type="submit" disabled={busy} data-testid="odf-save"
              className="bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-1.5 rounded text-xs font-semibold">
              {busy ? 'Saving…' : mode === 'add' ? 'Add OD' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function TxModal({ od, onClose, onSaved }) {
  const [type, setType]     = useState('draw');
  const [date, setDate]     = useState(today());
  const [amount, setAmount] = useState('');
  const [notes, setNotes]   = useState('');
  const [busy, setBusy]     = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/overdrafts/${od.id}/transactions`,
        { type, date, amount: parseFloat(amount) || 0, notes },
        { withCredentials: true });
      toast.success(`${TX_STYLE[type].label} ₹${amount}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`OD transaction · ${od.lender_name}`} onClose={onClose}>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <Card label="Used" val={fmt(od.current_utilization)} testId="tx-used"/>
        <Card label="Available" val={fmt(od.available_balance)} color="text-green-400" testId="tx-avail"/>
        <Card label="Rate" val={`${od.annualized_rate}% pa`} testId="tx-rate"/>
      </div>
      <form onSubmit={submit} className="space-y-3" data-testid="od-tx-form">
        <div>
          <label className="text-zinc-400 text-[10px] block mb-1">Type *</label>
          <div className="flex gap-1">
            {['draw', 'repay', 'interest_charge'].map(t => {
              const st = TX_STYLE[t];
              return (
                <button key={t} type="button" onClick={() => setType(t)} data-testid={`tx-type-${t}`}
                  className={`flex-1 text-xs px-2 py-1.5 rounded border inline-flex items-center justify-center gap-1 ${type === t ? st.bg + ' ' + st.color + ' font-semibold' : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700'}`}>
                  <st.icon size={11}/> {st.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-zinc-400 text-[10px] block mb-0.5">Date *</label>
            <input required type="date" value={date} onChange={e => setDate(e.target.value)} className={inputCls}/></div>
          <div><label className="text-zinc-400 text-[10px] block mb-0.5">Amount (₹) *</label>
            <input required type="number" step="any" value={amount} onChange={e => setAmount(e.target.value)}
              data-testid="tx-amount" className={inputCls}/></div>
        </div>
        <div><label className="text-zinc-400 text-[10px] block mb-0.5">Notes</label>
          <input value={notes} onChange={e => setNotes(e.target.value)} className={inputCls}/></div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-zinc-400">Cancel</button>
          <button type="submit" disabled={busy} data-testid="tx-save"
            className={`px-4 py-1.5 rounded text-xs font-semibold ${TX_STYLE[type].bg} ${TX_STYLE[type].color}`}>
            {busy ? 'Saving…' : `Record ${TX_STYLE[type].label}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function OdDetail({ od, onClose, onRecordTx, onEdit, onChanged }) {
  const [d, setD] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [statements, setStatements] = useState([]);
  const reload = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/overdrafts/${od.id}`, { withCredentials: true, bypassCache: true }),
        axios.get(`${API}/overdrafts/${od.id}/statements`, { withCredentials: true, bypassCache: true }),
      ]);
      setD(r.data);
      setStatements(s.data || []);
    } catch {/* */}
  }, [od.id]);
  useEffect(() => { reload(); }, [reload]);
  const removeTx = async (tid) => {
    if (!window.confirm('Delete this transaction? Cashflow row will be reversed.')) return;
    await axios.delete(`${API}/overdrafts/${od.id}/transactions/${tid}`, { withCredentials: true });
    reload(); onChanged();
  };

  // iter197 — Upload bank statement → auto-create OD transactions
  const handleStatementUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset so re-selecting the same file refires
    if (!window.confirm(
      `Upload ${file.name} for ${od.lender_name}?\n\n` +
      `Each line will be classified as Draw, Repay, or Interest Charge based on credit/debit and narration keywords. ` +
      `A matching cashflow row will be created for each. Re-uploading the same file is a no-op (deduplicated by file hash).`
    )) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/overdrafts/${od.id}/upload-statement`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const s = r.data.summary || {};
      toast.success(
        `Imported ${r.data.statement.lines_imported} transactions · ` +
        `${s.draw || 0} draws · ${s.repay || 0} repays · ${s.interest_charge || 0} interest/charges`
      );
      reload();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const removeStatement = async (sid) => {
    if (!window.confirm('Delete this statement?\n\nALL transactions + cashflow rows imported from it will be removed. The OD utilization will restore to its pre-upload state.')) return;
    try {
      const r = await axios.delete(`${API}/overdrafts/${od.id}/statements/${sid}`, { withCredentials: true });
      toast.success(`Removed ${r.data.transactions_removed} transactions`);
      reload(); onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  if (!d) return <Modal title="Loading…" onClose={onClose}><div className="py-8 text-center text-zinc-500 text-sm">Loading…</div></Modal>;
  return (
    <Modal title={`OD · ${d.lender_name}`} onClose={onClose} wide>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <Card label="Limit" val={fmt(d.sanctioned_limit)}/>
        <Card label="Used" val={fmt(d.current_utilization)} hot={d.utilization_pct > 80}/>
        <Card label="Available" val={fmt(d.available_balance)} color="text-green-400"/>
        <Card label="Accrued" val={fmt(d.accrued_interest_unpaid)} color="text-amber-400"/>
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button onClick={onRecordTx} className="inline-flex items-center gap-1 text-xs bg-cyan-500 hover:bg-cyan-400 text-black px-3 py-1.5 rounded font-semibold" data-testid="od-record-tx-btn">
          <IndianRupee size={11}/> Record Transaction
        </button>
        <label className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded font-semibold cursor-pointer ${uploading ? 'bg-zinc-800 text-zinc-500' : 'bg-purple-500 hover:bg-purple-400 text-white'}`} data-testid="od-upload-stmt-btn">
          <Upload size={11}/>
          {uploading ? 'Importing…' : 'Upload Statement'}
          <input type="file" accept=".xls,.xlsx,.csv" className="hidden" onChange={handleStatementUpload} disabled={uploading} data-testid="od-upload-stmt-input"/>
        </label>
        <button onClick={onEdit} className="inline-flex items-center gap-1 text-xs border border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-cyan-500 px-3 py-1.5 rounded">
          <Pencil size={11}/> Edit
        </button>
        <span className="text-xs text-zinc-500">{d.lender_type} · {d.purpose} · {d.annualized_rate}% pa · {d.utilization_pct}% used</span>
      </div>

      {/* iter197 — Imported statements list (so operator can delete + re-import) */}
      {statements.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden mb-3" data-testid="od-statements-section">
          <div className="px-3 py-2 border-b border-zinc-800 text-zinc-300 text-xs font-semibold inline-flex items-center gap-1.5">
            <FileText size={11}/> Imported Statements ({statements.length})
          </div>
          <div className="max-h-32 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-800/40 text-zinc-500 text-[10px] uppercase">
                <tr>
                  <th className="px-2 py-1.5 text-left">Filename</th>
                  <th className="px-2 py-1.5 text-left">Period</th>
                  <th className="px-2 py-1.5 text-right">Lines</th>
                  <th className="px-2 py-1.5 text-left">Breakdown</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {statements.map(s => (
                  <tr key={s.id} className="border-b border-zinc-800/40">
                    <td className="px-2 py-1.5 text-zinc-300 truncate max-w-[180px]">{s.filename}</td>
                    <td className="px-2 py-1.5 font-mono text-zinc-400">{s.period_start} → {s.period_end}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-zinc-100">{s.lines_imported}</td>
                    <td className="px-2 py-1.5 text-[10px]">
                      <span className="text-cyan-400">D:{s.draws_count}</span>{' '}
                      <span className="text-green-400">R:{s.repays_count}</span>{' '}
                      <span className="text-red-400">I:{s.interest_count}</span>
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <button onClick={() => removeStatement(s.id)} className="text-red-400 hover:text-red-300" title="Delete statement + all imported transactions">
                        <Trash2 size={10}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-zinc-800 text-zinc-300 text-xs font-semibold">
          Transactions ({d.transactions.length})
        </div>
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-800/40 text-zinc-500 text-[10px] uppercase">
              <tr><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Type</th><th className="px-2 py-1.5 text-right">Amount</th><th className="px-2 py-1.5 text-left">Notes</th><th></th></tr>
            </thead>
            <tbody>
              {d.transactions.length === 0 ? (
                <tr><td colSpan={5} className="py-4 text-center text-zinc-600">No transactions yet</td></tr>
              ) : d.transactions.slice().reverse().map(t => {
                const st = TX_STYLE[t.type];
                return (
                  <tr key={t.id} className="border-b border-zinc-800/40">
                    <td className="px-2 py-1.5 font-mono text-zinc-300">{t.date}</td>
                    <td className="px-2 py-1.5"><span className={`text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${st.bg} ${st.color}`}><st.icon size={9}/> {st.label}</span></td>
                    <td className={`px-2 py-1.5 text-right font-mono ${st.color}`}>{fmt(t.amount)}</td>
                    <td className="px-2 py-1.5 text-zinc-400 truncate max-w-[200px]">{t.notes || '—'}</td>
                    <td className="px-2 py-1.5 text-right"><button onClick={() => removeTx(t.id)} className="text-red-400 hover:text-red-300"><Trash2 size={10}/></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {d.audit_log && d.audit_log.length > 0 && (
        <div className="mt-3 bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-zinc-800 text-zinc-300 text-xs font-semibold inline-flex items-center gap-1.5"><History size={11}/> Change Log</div>
          <div className="max-h-40 overflow-y-auto p-2 space-y-2 text-xs">
            {d.audit_log.slice().reverse().map((a, i) => (
              <div key={i} className="bg-zinc-950 border border-zinc-800 rounded p-2">
                <div className="text-amber-400 font-mono text-[10px]">{(a.ts || '').slice(0, 19).replace('T', ' ')} · by {a.user}</div>
                <div className="text-zinc-300 text-[10px] mt-0.5">
                  <span className="bg-zinc-800 px-1 rounded mr-1">{a.action}</span>
                  {Array.isArray(a.details) ? a.details.map((c, j) =>
                    <div key={j}>{c.field}: <span className="text-red-300 line-through">{String(c.old)}</span> → <span className="text-green-300">{String(c.new)}</span></div>
                  ) : JSON.stringify(a.details)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Modal({ title, onClose, wide, children }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className={`bg-zinc-950 border border-zinc-800 rounded-xl ${wide ? 'max-w-3xl' : 'max-w-xl'} w-full max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-950 z-10">
          <h3 className="text-zinc-100 text-sm font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 rounded text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"><X size={14}/></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
